<template>
  <div class="container-with-slider-navigation">
    <div class="search-container" @click="changeModalStatusOnMobile">
      <SiteSearch
        class="ignore"
        data-test="main-page-search"
        :SEARCH_API_URL="VITE_RUNTIME_SEARCH_API_URL"
        :SOGU_URL="VITE_RUNTIME_SOGU_SERVICE_URL"
        :AUDIOGUIDES_URL="VITE_RUNTIME_AUDIOGUIDES_URL"
        sogu-prefix="main"
        :variant="SiteSearchVariant.Main"
      />
    </div>
    <slot />
  </div>

  <UiModal
    :is-shown="isModalShow"
    :teleport-target="modalTarget || undefined"
    :title="t('modalTitle')"
    size="fullscreen"
    modal-class="main-page-search-modal"
    is-content-no-padding
    is-footer-hidden
    @close="changeModalStatus"
  >
    <template #content>
      <SiteSearch
        ref="searchRef"
        sogu-prefix="header"
        :SEARCH_API_URL="VITE_RUNTIME_SEARCH_API_URL"
        :SOGU_URL="VITE_RUNTIME_SOGU_SERVICE_URL"
        :AUDIOGUIDES_URL="VITE_RUNTIME_AUDIOGUIDES_URL"
        :is-opened="true"
        :force-show-dropdown="true"
        class="mobile-search"
      />
    </template>
  </UiModal>
</template>

<script lang="ts" setup>
import { UiModal } from '@experience/core-modal-ui';
import { useI18n } from '@experience/i18n';
import { SiteSearch, SiteSearchVariant } from '@experience/site-search-package';
import { ref, onMounted } from 'vue';

import ru from '@/features/mainPageSearch/i18n/ru';
import { getRuntimeConfig } from '@/shared/config/runtime';
import { useAnalytics } from '@/shared/lib/analytics';
import { initModalTeleportTarget } from '@/shared/lib/initModalTeleportTarget';
import { useScreenSize } from '@/shared/utils/breakpoints';

type MessageSchema = typeof ru;
const { t } = useI18n<{ message: MessageSchema }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const { VITE_RUNTIME_SOGU_SERVICE_URL, VITE_RUNTIME_SEARCH_API_URL, VITE_RUNTIME_AUDIOGUIDES_URL } = getRuntimeConfig();
const { isMobile } = useScreenSize();
const { trackEvent } = useAnalytics();

const isModalShow = ref(false);
const modalTarget = ref<HTMLElement | null>(null);

const changeModalStatus = () => {
  if (!isMobile.value) {
    return;
  }

  isModalShow.value = !isModalShow.value;
};

const changeModalStatusOnMobile = () => {
  if (isMobile.value) {
    void trackEvent('main:search:modal.open');
    changeModalStatus();
  }
};

onMounted(() => {
  initModalTeleportTarget(modalTarget);
});
</script>

<style lang="scss" scoped>
.ignore {
  @include adaptivity.mobile {
    pointer-events: none;
  }
}

.mobile-search {
  padding: 5px 16px 0;

  :deep(.dropdown-content > .group-header:first-child),
  :deep(.group-header-skeleton:not(.group-header-skeleton--compact-top)) {
    padding-top: 0;
  }

  :deep(.destinations-link) {
    padding-right: var(--alias-space-16);
    padding-left: var(--alias-space-16);
  }
}

// и поиск на странице, и поиск в модалке
:deep(.dropdown-content > .destinations-link:first-child) {
  border-top: none;
}

.search-container {
  display: flex;
  justify-content: center;

  @include adaptivity.mobile {
    width: 100%;
  }
}

.container-with-slider-navigation {
  @include adaptivity.tablet {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  @include adaptivity.mobile {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
    width: 100%;
  }
}
</style>

<style lang="scss">
.main-page-search-modal.ui-modal {
  .ui-modal-header.ui-modal-header--fixed::after {
    display: none;
  }
}
</style>
